import React, { useMemo, useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { TextField, MenuItem, Grid, Button, Typography, Box, CircularProgress, IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { toastr } from 'react-redux-toastr';
import { uniqueId } from 'lodash';
import { filesize } from 'filesize';

import TituloPagina from '../../components/layout/TituloPagina';
import FileList from '../../components/templates/fileList/FileList';
import If from '../../components/condicional/if';
import { addNotificacaoPush, getNotificacaoPush, carregarUploadedFiles } from '../../store/actions/notificacoesPush';
import LoadingOverlay from '../../components/layout/LoadingOverlay';

import {
    Container,
    Content,
    DropContainer,
    UploadMessage
} from './styles';

const targetTypeOptions = [
    { value: 'ALL_USERS', label: 'Todos os usuários' },
    { value: 'SPECIFIC_USER', label: 'Usuário específico' },
    { value: 'ROLE', label: 'Perfil de usuário' },
];

const actionTypeOptions = [
    { value: 'OPEN_SCREEN', label: 'Abrir tela' },
    { value: 'EXTERNAL_URL', label: 'Abrir URL externa' },
    { value: 'READ_ONLY', label: 'Somente leitura' },
];

const MAX_SIZE = 1500000; // 1,5mb
const MAX_FILES = 1;

const NotificacoesPushCadastro = props => {
    const history = useHistory();
    const location = useLocation();
    const {
        addNotificacaoPush,
        getNotificacaoPush,
        notificacaoPush,
        loading,
        processing,
        uploadedFiles,
        carregarUploadedFiles
    } = props;
    const [processingMessage, setProcessingMessage] = useState('');
    const notificacaoId = Number(location?.state?.notificacaoId);
    const [disableFields, setDisableFields] = useState(false);
    const isDetailRoute = location.pathname === '/notificacoes-push/detalhar-notificacao';
    const [imagemAnexada, setImagemAnexada] = useState(false);
    const [urlImagemExistente, setUrlImagemExistente] = useState(null);

    useEffect(() => {
        if (isDetailRoute && !notificacaoId) {
            history.replace('/notificacoes-push');
            return;
        }

        if (isDetailRoute) {
            setDisableFields(true);
        }

        if (notificacaoId > 0) {
            getNotificacaoPush(notificacaoId);
        }
    }, [getNotificacaoPush, history, isDetailRoute, notificacaoId]);

    const handleUpload = (files) => {
        const currentCount = uploadedFiles.length;

        if (currentCount >= MAX_FILES) {
            toastr.error('Erro', 'Limite de 1 imagem atingido.');
            return;
        }

        const filesToProcess = files.slice(0, MAX_FILES - currentCount);

        if (files.length > (MAX_FILES - currentCount)) {
            toastr.warning('Aviso', 'Alguns arquivos foram ignorados (limite de 1).');
        }

        const newUploads = filesToProcess.map((file, index) => ({
            file,
            idTemp: uniqueId('upload_'), // ID único por ARQUIVO (essencial para progresso)
            readableSize: filesize(file.size),
            preview: URL.createObjectURL(file),
            progress: 0,
            uploaded: false,
            error: false,
            url: null
        }));

        // Atualiza a lista no Redux
        carregarUploadedFiles([...uploadedFiles, ...newUploads]);

        setImagemAnexada(true);
    };

    const handleRemoverImagemExistente = () => {
        setUrlImagemExistente(null);
        setImagemAnexada(false);
        formik.setFieldValue('urlImagem', '');
    };

    const validationSchema = useMemo(() => yup.object({
        titulo: yup
            .string()
            .trim()
            .required('Informe o título da notificação.')
            .min(3, 'O título deve possuir ao menos 3 caracteres.')
            .max(120, 'O título deve possuir no máximo 120 caracteres.'),
        conteudo: yup
            .string()
            .trim()
            .required('Informe o conteúdo da notificação.')
            .min(5, 'O conteúdo deve possuir ao menos 5 caracteres.')
            .max(500, 'O conteúdo deve possuir no máximo 500 caracteres.'),
        targetType: yup
            .string()
            .trim()
            .oneOf(targetTypeOptions.map(option => option.value), 'Selecione um tipo de público válido.')
            .required('Informe o tipo de público.'),
        targetId: yup.string().trim().max(120, 'O identificador deve possuir no máximo 120 caracteres.'),
        actionType: yup
            .string()
            .transform((value, originalValue) => originalValue === '' ? null : value)
            .oneOf(actionTypeOptions.map(option => option.value), 'Selecione uma ação válida.')
            .nullable(),
        actionPayload: yup
            .string()
            .trim()
            .test('json-valido', 'Informe um JSON válido.', value => {
                if (!value) return true;

                try {
                    JSON.parse(value);
                    return true;
                } catch (error) {
                    return false;
                }
            }),
    }), []);

    const buildInitialValues = () => ({
        titulo: notificacaoPush?.titulo || '',
        conteudo: notificacaoPush?.conteudo || '',
        conteudoExpandido: notificacaoPush?.conteudoExpandido || '',
        imagemBanner: notificacaoPush?.imagemBanner || '',
        targetType: notificacaoPush?.targetType || '',
        targetId: notificacaoPush?.targetId || '',
        actionType: notificacaoPush?.actionType || '',
        actionPayload: notificacaoPush?.actionPayload || '',
    });

    const normalizePayload = (values) => ({
        titulo: values.titulo.trim(),
        conteudo: values.conteudo.trim(),
        conteudoExpandido: values.conteudoExpandido.trim(),
        targetType: values.targetType.trim(),
        targetId: values.targetId.trim(),
        file: values.file,
        actionType: values.actionType || null,
        actionPayload: values.actionPayload.trim(),
    });

    const formik = useFormik({
        initialValues: buildInitialValues(),
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setProcessingMessage('Enviando notificação...');

            const file = uploadedFiles[0];
            const newValues = { ...values, ...file }

            const success = await addNotificacaoPush(normalizePayload(newValues));

            setProcessingMessage('');
            setDisableFields(true);

            if (success) {
                history.push('/notificacoes-push');
            }
        },
        enableReinitialize: true,
    });

    const handleTargetTypeChange = (event) => {
        formik.handleChange(event);

        if (event.target.value === 'ALL_USERS') {
            formik.setFieldValue('targetId', '');
        }
    };

    useEffect(() => {
        if (notificacaoPush?.imagemBanner) {
            setUrlImagemExistente(notificacaoPush.imagemBanner);
            setImagemAnexada(true);
        }
    }, [notificacaoPush]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, height: '70vh' }}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    return (
        <>
            <TituloPagina
                titulo={isDetailRoute ? 'Detalhe da Notificação Push' : 'Enviar Notificação Push'}
                subtitulo={new Date().toLocaleString('pt-BR')}
            />

            {
                !isDetailRoute && (
                    <Grid container style={{ paddingTop: '15px', paddingBottom: '15px' }}>
                        <Typography variant='body1' component='p'>
                            Preencha os dados da notificação, defina o público e, se necessário, configure a ação de navegação
                            em formato JSON.
                        </Typography>
                    </Grid>
                )
            }

            <Container>
                <Content>
                    <If test={Boolean(urlImagemExistente)}>
                        <Grid container item xs={12} style={{ paddingBottom: '15px' }}>
                            <Grid item style={{ position: 'relative', width: '100%', maxWidth: 400 }}>
                                <img
                                    src={urlImagemExistente}
                                    alt='Imagem da campanha'
                                    style={{ width: '100%', borderRadius: 4, display: 'block' }}
                                />
                                <If test={!disableFields}>
                                    <IconButton
                                        onClick={handleRemoverImagemExistente}
                                        style={{ position: 'absolute', top: 4, right: 4, backgroundColor: '#fff' }}
                                        title='Excluir imagem'
                                    >
                                        <DeleteIcon color='error' />
                                    </IconButton>
                                </If>
                            </Grid>
                        </Grid>
                    </If>

                    <If test={!urlImagemExistente}>
                        <Dropzone
                            accept={{ 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] }}
                            onDrop={handleUpload}
                            maxSize={MAX_SIZE}
                        >
                            {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
                                <DropContainer
                                    {...getRootProps()}
                                    $isDragActive={isDragActive}
                                    $isDragReject={isDragReject}
                                >
                                    <input {...getInputProps()} />
                                    {!isDragActive && <UploadMessage>Clique ou arraste imagens aqui.</UploadMessage>}
                                    {isDragActive && !isDragReject && <UploadMessage type="success">Solte para enviar!</UploadMessage>}
                                    {isDragReject && <UploadMessage type="error">Arquivo não suportado ou muito grande.</UploadMessage>}
                                </DropContainer>
                            )}
                        </Dropzone>

                        {uploadedFiles.length > 0 && (
                            <FileList
                                files={uploadedFiles}
                            />
                        )}
                    </If>

                    <form onSubmit={formik.handleSubmit} noValidate autoComplete='off' style={{ width: 'inherit' }}>
                        <div style={{ flexGrow: 1, paddingTop: '15px' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id='titulo'
                                        name='titulo'
                                        label='Título'
                                        variant='filled'
                                        disabled={disableFields}
                                        value={formik.values.titulo}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.titulo && Boolean(formik.errors.titulo)}
                                        helperText={formik.touched.titulo && formik.errors.titulo}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id='conteudo'
                                        name='conteudo'
                                        label='Conteúdo'
                                        variant='filled'
                                        multiline
                                        rows={2}
                                        disabled={disableFields}
                                        value={formik.values.conteudo}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.conteudo && Boolean(formik.errors.conteudo)}
                                        helperText={formik.touched.conteudo && formik.errors.conteudo}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id='conteudoExpandido'
                                        name='conteudoExpandido'
                                        label='Conteúdo Expandido'
                                        variant='filled'
                                        multiline
                                        rows={5}
                                        disabled={disableFields}
                                        value={formik.values.conteudoExpandido}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.conteudoExpandido && Boolean(formik.errors.conteudoExpandido)}
                                        helperText={formik.touched.conteudoExpandido && formik.errors.conteudoExpandido}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        id='targetType'
                                        name='targetType'
                                        label='Público de envio'
                                        variant='filled'
                                        disabled={disableFields}
                                        value={formik.values.targetType}
                                        onChange={handleTargetTypeChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.targetType && Boolean(formik.errors.targetType)}
                                        helperText={formik.touched.targetType && formik.errors.targetType}
                                    >
                                        <MenuItem value=''>Selecione o público</MenuItem>
                                        {targetTypeOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        id='targetId'
                                        name='targetId'
                                        label='Identificador do público'
                                        variant='filled'
                                        disabled={disableFields || formik.values.targetType === 'ALL_USERS'}
                                        value={formik.values.targetId}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.targetId && Boolean(formik.errors.targetId)}
                                        helperText={formik.touched.targetId && formik.errors.targetId
                                            ? formik.errors.targetId
                                            : 'Informe o ID do usuário ou o nome do perfil selecionado.'}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant='subtitle1' component='p'>Ação ao abrir a notificação</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        id='actionType'
                                        name='actionType'
                                        label='Tipo de ação'
                                        variant='filled'
                                        disabled={disableFields}
                                        value={formik.values.actionType}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.actionType && Boolean(formik.errors.actionType)}
                                        helperText={formik.touched.actionType && formik.errors.actionType}
                                    >
                                        <MenuItem value=''>Nenhuma ação</MenuItem>
                                        {actionTypeOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id='actionPayload'
                                        name='actionPayload'
                                        label='Dados da ação (JSON)'
                                        variant='filled'
                                        multiline
                                        rows={5}
                                        disabled={disableFields}
                                        value={formik.values.actionPayload}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.actionPayload && Boolean(formik.errors.actionPayload)}
                                        helperText={formik.touched.actionPayload && formik.errors.actionPayload
                                            ? formik.errors.actionPayload
                                            : 'Ex.: {"screen":"OrderDetails", "id":"123", "ctaText":"Saber mais", "ctaUrl":"https://..."}'}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container pt={4} style={{ paddingTop: '25px', paddingBottom: '25px' }}>
                                <If test={!disableFields}>
                                    <Grid item style={{ paddingRight: '15px' }}>
                                        <Button type='submit' color='secondary' variant='contained' disabled={formik.isSubmitting}>Enviar</Button>
                                    </Grid>
                                    <Grid item style={{ paddingRight: '15px' }}>
                                        <Button variant='contained' onClick={() => history.push('/notificacoes-push')}>Cancelar</Button>
                                    </Grid>
                                </If>
                                <If test={disableFields}>
                                    <Grid item style={{ paddingRight: '15px' }}>
                                        <Button color='secondary' variant='contained' onClick={() => history.push('/notificacoes-push')}>Voltar</Button>
                                    </Grid>
                                </If>
                            </Grid>
                        </div>
                    </form>
                </Content>
            </Container>

            <LoadingOverlay
                open={processing}
                message={processingMessage}
            />
        </>
    )
}

const mapStateToProps = state => ({
    notificacaoPush: state.notificacoesPush.notificacaoPush,
    processing: state.notificacoesPush.processing,
    loading: state.notificacoesPush.loading,
    uploadedFiles: state.notificacoesPush.uploadedFiles,
});

const mapDispatchToProps = dispatch => bindActionCreators({
    addNotificacaoPush,
    getNotificacaoPush,
    carregarUploadedFiles,
}, dispatch
);

export default connect(mapStateToProps, mapDispatchToProps)(NotificacoesPushCadastro);